// The one place a rolling-date token becomes words.
//
// A rolling date is authored and stored as a token (`@today-7d`,
// `@startOfQuarter`) because that is what the engines resolve — but a token is
// a wire format, not a reading. Every surface that shows one to a person goes
// through `humanizeRollingDateToken`: the preset controls
// (`RollingDatePresetsMolecule`), the row's closed summary and the tree's
// "Reads as" sentence (`filterLaneSummaries.ts`). One function, so a preset
// cannot be labelled one way in the editor and another in the summary.
//
// The grammar itself is NOT re-implemented here: parsing comes from
// `@center-platform/spheres/filters`, the same module the server resolves
// through, so a token this file can read is by construction a token the
// engines can resolve.
import {
  ROLLING_DATE_ANCHORS,
  ROLLING_DATE_UNITS,
  isRollingDateToken,
  parseRollingDateToken,
  type ParsedRollingDate,
  type RollingDateAnchor,
  type RollingDateToken,
  type RollingDateUnit,
} from '@center-platform/spheres/filters';
import type { TranslateFn } from './filterOperators';

export const ROLLING_DATE_KEY = 'filters.rollingDate';

/** `'@startOfWeek'` → `'startOfWeek'` — the anchor's i18n key segment. */
export function anchorKey(anchor: RollingDateAnchor): string {
  return anchor.slice(1);
}

/** The anchor's label as a control reads it — Title Case, standalone ("Start of week"). */
export function rollingDateAnchorLabel(anchor: RollingDateAnchor, t: TranslateFn): string {
  return t(`${ROLLING_DATE_KEY}.anchors.${anchorKey(anchor)}`);
}

/**
 * The anchor as it reads INSIDE a sentence ("7 days before the start of the
 * quarter"). A separate key set from {@link rollingDateAnchorLabel} rather than a
 * lower-cased version of it: French needs its own article and elision
 * ("le début du trimestre"), which no mechanical transform of a button label
 * can produce.
 */
function anchorPhrase(anchor: RollingDateAnchor, t: TranslateFn): string {
  return t(`${ROLLING_DATE_KEY}.anchorPhrase.${anchorKey(anchor)}`);
}

/** A unit as a standalone control label ("Weeks"). */
export function rollingDateUnitLabel(unit: RollingDateUnit, t: TranslateFn): string {
  return t(`${ROLLING_DATE_KEY}.unitNames.${unit}`);
}

/** A unit as it reads after a count ("1 week" / "3 weeks") — pluralized by i18next. */
function unitWithCount(unit: RollingDateUnit, count: number, t: TranslateFn): string {
  return t(`${ROLLING_DATE_KEY}.units.${unit}`, { count });
}

/**
 * A parsed rolling date as the words a reader sees.
 *
 * A bare anchor (or an offset of zero, which resolves to the same instant)
 * reads as the anchor alone; anything else reads as the full relative phrase.
 * Note the phrasing follows the GRAMMAR's evaluation order — the offset moves
 * the reference point and the anchor then snaps it — so `@startOfMonth-1m`
 * reads "1 month before the start of the month", which is the start of last
 * month, exactly as it resolves.
 */
export function humanizeParsedRollingDate(parsed: ParsedRollingDate, t: TranslateFn): string {
  if (parsed.unit === null || parsed.amount === 0) {
    return rollingDateAnchorLabel(parsed.anchor, t);
  }
  return t(`${ROLLING_DATE_KEY}.${parsed.sign === -1 ? 'offsetBefore' : 'offsetAfter'}`, {
    count: parsed.amount,
    unit: unitWithCount(parsed.unit, parsed.amount, t),
    anchor: anchorPhrase(parsed.anchor, t),
  });
}

/**
 * A rolling-date token as the words a reader sees, or `null` when `value` is not
 * a rolling date.
 *
 * `null` is the caller's signal to render the value some other way (a formatted
 * calendar date, a literal). It must never be turned into a fallback reading of
 * the raw token: printing `@todya-7d` as if it were a date would tell the author
 * a malformed filter is fine.
 */
export function humanizeRollingDateToken(value: unknown, t: TranslateFn): string | null {
  const parsed = parseRollingDateToken(value);
  return parsed ? humanizeParsedRollingDate(parsed, t) : null;
}

/** Re-exported so consumers need one import for "is this a token" + "what does it say". */
export { isRollingDateToken };

/** Composes a token from its parts. Returns the bare anchor when the offset is zero. */
export function buildRollingDateToken(
  anchor: RollingDateAnchor,
  sign: 1 | -1,
  amount: number,
  unit: RollingDateUnit
): RollingDateToken {
  if (!Number.isInteger(amount) || amount <= 0) return anchor;
  // The grammar caps the magnitude at 9999 and forbids leading zeros — the token
  // string is scanned verbatim into the Redis cache key, so two spellings of one
  // date would key two entries. `String(number)` produces the one canonical form.
  const bounded = Math.min(amount, 9999);
  return `${anchor}${sign === -1 ? '-' : '+'}${bounded}${unit}` as RollingDateToken;
}

/**
 * The quick presets offered ahead of the calendar, in the order the design
 * states them (`context/filter-row.jsx:192` `EF_DATE_PRESETS`). Every one is a
 * real token the engines resolve — this list is a shortcut through the grammar,
 * never an extension of it, so a preset can never write a value a query cannot
 * evaluate.
 *
 * `labelKey` is `null` where the token's own reading is already the label
 * ("Today", "Start of week"); the three offsets get a phrase of their own
 * because "7 days after today" is a worse button than "In 7 days".
 */
export interface RollingDateQuickPreset {
  token: RollingDateToken;
  /** An override label key, or `null` to read the token through the humanizer. */
  labelKey: string | null;
}

export const ROLLING_DATE_QUICK_PRESETS: readonly RollingDateQuickPreset[] = [
  { token: '@today', labelKey: null },
  { token: '@startOfWeek', labelKey: null },
  { token: '@startOfQuarter', labelKey: null },
  { token: '@today+7d', labelKey: `${ROLLING_DATE_KEY}.presets.plus7d` },
  { token: '@today+30d', labelKey: `${ROLLING_DATE_KEY}.presets.plus30d` },
  { token: '@today-30d', labelKey: `${ROLLING_DATE_KEY}.presets.minus30d` },
];

/** A quick preset's button label. */
export function quickPresetLabel(preset: RollingDateQuickPreset, t: TranslateFn): string {
  if (preset.labelKey) return t(preset.labelKey);
  return humanizeRollingDateToken(preset.token, t) ?? preset.token;
}

export { ROLLING_DATE_ANCHORS, ROLLING_DATE_UNITS };
export type { RollingDateAnchor, RollingDateToken, RollingDateUnit, ParsedRollingDate };
